import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import omnichannelService, {
  FacebookPage,
  PaginationMeta
} from '../../services/omnichannelService';

export interface FacebookPageSelection {
  id: string;
  access_token: string;
  name?: string;
}

interface FacebookPageConnectorProps {
  onBack: () => void;
}

export default function FacebookPageConnector({ onBack }: FacebookPageConnectorProps) {
  const { theme } = useTheme();
  const PAGE_LIMIT = 10;
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [selectedPages, setSelectedPages] = useState<FacebookPageSelection[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInstallingPages, setIsInstallingPages] = useState(false);
  const [isRelinkingFacebook, setIsRelinkingFacebook] = useState(false);
  const [installMessage, setInstallMessage] = useState<string | null>(null);
  const [installError, setInstallError] = useState<string | null>(null);

  const [installedPages, setInstalledPages] = useState<string[]>([]);

  const loadPages = async (page: number = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const fetchedPages = await omnichannelService.getFacebookPages(page, PAGE_LIMIT);
      const normalizedPages = Array.isArray(fetchedPages.data) ? fetchedPages.data : [];
      setPages(normalizedPages);
      setPagination(fetchedPages.pagination || null);
      setCurrentPage(fetchedPages.pagination?.page || page);

      const selectedPages = normalizedPages.filter((p) => p.subscribed);

      setSelectedPages(selectedPages);
      setInstalledPages(selectedPages.map((p) => p.id));
    } catch (err: any) {
      console.error('Failed to load Facebook pages:', err);
      setError('Failed to load Facebook pages. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePageSelection = (page: FacebookPage) => {
    setSelectedPages((prev) => {
      const alreadySelected = prev.find((p) => p.id === page.id);
      const next = alreadySelected
        ? prev.filter((p) => p.id !== page.id)
        : [
            ...prev,
            {
              id: page.id,
              access_token: page.access_token,
              name: page.name
            }
          ];
      return next;
    });
  };

  const handleInstallPages = async () => {
    if (selectedPages.length === 0) {
      setInstallError('Please select at least one page.');
      return;
    }

    setIsInstallingPages(true);
    setInstallMessage(null);
    setInstallError(null);
    try {
      await omnichannelService.subscribeFacebookPages(
        selectedPages.map((page) => ({
          page_id: page.id,
          access_token: page.access_token
        }))
      );
      setInstallMessage('Successfully enabled FlowCXP on the selected pages.');
      setInstalledPages(selectedPages.map((page) => page.id));
    } catch (error: any) {
      console.error('Failed to install FlowCXP app on Facebook pages:', error);
      setInstallError(error.message || 'Failed to install FlowCXP on the selected pages.');
    } finally {
      setIsInstallingPages(false);
    }
  };

  const handleRelinkFacebook = async () => {
    setIsRelinkingFacebook(true);
    setError(null);
    try {
      const loginResult = await omnichannelService.requestFacebookLogin();
      if (loginResult.success && loginResult.data) {
        window.location.href = loginResult.data;
        return;
      }

      throw new Error('Failed to initiate Facebook login');
    } catch (relinkError: any) {
      console.error('Failed to relink Facebook:', relinkError);
      setError(relinkError.message || 'Unable to redirect to Facebook login');
      setIsRelinkingFacebook(false);
    }
  };

  const handleUninstallPage = async (id: string, accessToken: string) => {
    setIsInstallingPages(true);
    try {
      await omnichannelService.unsubscribeFacebookPages(id, accessToken);
      setSelectedPages((prev) => prev.filter((p) => p.id !== id));
      setInstalledPages((prev) => prev.filter((p) => p !== id));
    } catch (error: any) {
      console.error('Failed to uninstall FlowCXP app on Facebook pages:', error);
      setInstallError(error.message || 'Failed to uninstall FlowCXP.');
    } finally {
      setIsInstallingPages(false);
    }
  };

  const isPageSelected = (pageId: string) => selectedPages.some((p) => p.id === pageId);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      );
    }

    if (pages.length === 0) {
      return (
        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          No Facebook pages found for this account.
        </p>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {pages.map((page) => {
          const isSelected = isPageSelected(page.id);
          const categoryName =
            page.category_list?.[0]?.name || page.category || 'Unspecified category';
          const isSubscribed = installedPages.includes(page.id);

          return (
            <div
              key={page.id}
              role="button"
              aria-pressed={isSelected}
              tabIndex={0}
              onClick={() => togglePageSelection(page)}
              className={`relative flex flex-col gap-3 rounded-lg border p-4 transition ${
                isSelected
                  ? theme === 'dark'
                    ? 'border-blue-500 bg-blue-900/20'
                    : 'border-blue-400 bg-blue-50'
                  : theme === 'dark'
                    ? 'border-gray-700 bg-gray-800 hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <label
                className="absolute right-3 top-3 flex cursor-pointer items-center gap-2 text-sm"
                onClick={(event) => event.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => togglePageSelection(page)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  aria-label={`Select ${page.name}`}
                />
              </label>

              <div className="flex items-start gap-3">
                {page.picture_url ? (
                  <img
                    src={page.picture_url}
                    alt={`${page.name} profile`}
                    className="h-12 w-12 rounded-md border border-gray-200 object-cover"
                  />
                ) : (
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full border text-sm font-semibold ${
                      theme === 'dark'
                        ? 'border-gray-600 bg-gray-700 text-gray-100'
                        : 'border-gray-200 bg-gray-100 text-gray-700'
                    }`}
                  >
                    {page.name?.charAt(0)?.toUpperCase() || 'P'}
                  </div>
                )}
                <div className="space-y-1 overflow-hidden">
                  <p
                    className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
                  >
                    {page.name}
                  </p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                    Page id - <code>{page.id}</code>
                  </p>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {categoryName}
                  </p>
                  {isSubscribed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUninstallPage(page.id, page.access_token);
                      }}
                      className={`my-4 flex items-center px-2 py-1 text-xs rounded-md ${
                        theme === 'dark'
                          ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-800 bg-gray-700'
                          : 'text-gray-700 hover:text-gray-900 hover:bg-gray-200 bg-gray-200'
                      }`}
                      disabled={isInstallingPages}
                    >
                      Unsubscribe
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={`space-y-4 rounded-lg border px-5 py-6 ${
        theme === 'dark' ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={onBack}
            className={`mb-4 inline-flex items-center px-3 py-2 text-sm font-medium rounded-md ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700'
                : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            ← Back
          </button>
          <div>
            <h3
              className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
            >
              Facebook Pages
            </h3>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Pick which pages should connect to FlowCXP.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => loadPages(currentPage)}
          className={`text-sm font-medium transition ${theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-500'}`}
        >
          Refresh
        </button>
      </div>

      {renderContent()}

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} items)
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => loadPages(currentPage - 1)}
              disabled={isLoading || !pagination.hasPrev}
              className={`rounded px-3 py-1 text-sm ${
                isLoading || !pagination.hasPrev
                  ? 'cursor-not-allowed opacity-50'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => loadPages(currentPage + 1)}
              disabled={isLoading || !pagination.hasNext}
              className={`rounded px-3 py-1 text-sm ${
                isLoading || !pagination.hasNext
                  ? 'cursor-not-allowed opacity-50'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex flex-col gap-4 items-start">
          <button
            type="button"
            onClick={handleInstallPages}
            disabled={isInstallingPages || !selectedPages.length}
            className={`rounded-lg p-3 font-semibold transition ${
              theme === 'dark'
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            Subscribe Pages to Atrium
          </button>
          <hr className="self-stretch" />
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Not seeing other pages? Link your facebook again to add more pages to use Atrium.
          </p>
          <button
            type="button"
            onClick={handleRelinkFacebook}
            disabled={isRelinkingFacebook}
            className={`rounded-lg border p-3 font-semibold transition ${
              theme === 'dark'
                ? 'border-gray-600 text-gray-200 hover:bg-gray-800'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            } ${isRelinkingFacebook ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {isRelinkingFacebook ? 'Redirecting to Facebook...' : 'Link Facebook again'}
          </button>
        </div>
      </div>
      {installError && <p className="text-sm text-red-500">{installError}</p>}
    </div>
  );
}
